import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useFormContext } from '../../../context/FormContext';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';
import { Select } from '../../ui/Select';

const { FiPlus, FiTrash2, FiPaperclip, FiX } = FiIcons;

const CHANNEL_OPTIONS = [
  { label: 'Direct Online', value: 'direct_online' },
  { label: 'In-Store / Retail', value: 'retail' },
  { label: 'Telephony', value: 'telephony' },
  { label: 'Third-Party Marketplace', value: 'marketplace' },
  { label: 'B2B Procurement', value: 'b2b' },
  { label: 'Other', value: 'other' }
];

export const TransactionsSection = () => {
  const { data, updateFormSection } = useFormContext();
  const transactions = data.form.transactions;

  const addTransaction = () => {
    updateFormSection('transactions', [
      ...transactions,
      { 
        id: Date.now(), 
        date: '', 
        customer: '', 
        product: '', 
        amount: '', 
        currency: 'GBP', 
        channel: '', 
        notes: '',
        supportingFiles: [] 
      }
    ]);
  };

  const removeTransaction = (id) => updateFormSection('transactions', transactions.filter(t => t.id !== id));
  
  const updateField = (id, field, value) => {
    updateFormSection('transactions', transactions.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const handleFileUpload = (id, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const transaction = transactions.find(t => t.id === id);
    const currentFiles = transaction.supportingFiles || [];
    updateField(id, 'supportingFiles', [...currentFiles, { name: file.name, size: file.size }]);
    // Reset file input target value so the same file could be added again if removed
    e.target.value = null;
  };

  const removeFile = (id, fileIndex) => {
    const transaction = transactions.find(t => t.id === id);
    const newFiles = [...(transaction.supportingFiles || [])];
    newFiles.splice(fileIndex, 1);
    updateField(id, 'supportingFiles', newFiles);
  };

  return (
    <div className="space-y-6">
      {transactions.map((t, index) => (
        <div key={t.id} className="p-6 border border-brand-border rounded-2xl bg-brand-light/20 relative group transition-all hover:bg-brand-light/40 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-accent text-white text-xs font-bold shadow-sm">
                {index + 1}
              </span>
              <h4 className="text-sm font-bold text-brand-text uppercase tracking-tight">Transaction Item</h4>
            </div>
            {transactions.length > 1 && (
              <button onClick={() => removeTransaction(t.id)} className="text-brand-muted hover:text-red-500 transition-colors p-1">
                <SafeIcon icon={FiTrash2} className="text-lg" />
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
            <Input label="Date" type="date" value={t.date} onChange={e => updateField(t.id, 'date', e.target.value)} />
            <Input label="Customer Name" value={t.customer} onChange={e => updateField(t.id, 'customer', e.target.value)} placeholder="e.g. Acme Corp" />
            <Input label="Product/Service" value={t.product} onChange={e => updateField(t.id, 'product', e.target.value)} placeholder="Service description" />
            
            <div className="flex gap-2">
              <Input label="Amount" type="number" className="flex-1" value={t.amount} onChange={e => updateField(t.id, 'amount', e.target.value)} placeholder="0.00" />
              <div className="w-28">
                <Select 
                  label="Currency" 
                  value={t.currency} 
                  onChange={val => updateField(t.id, 'currency', val)}
                  options={[{ label: 'GBP (£)', value: 'GBP' }, { label: 'EUR (€)', value: 'EUR' }]}
                />
              </div>
            </div>

            <Select 
              label="Purchase Channel" 
              value={t.channel} 
              onChange={val => updateField(t.id, 'channel', val)}
              options={CHANNEL_OPTIONS}
              placeholder="Select channel"
            />
            <Input label="Internal Notes" value={t.notes} onChange={e => updateField(t.id, 'notes', e.target.value)} placeholder="Optional context" />
          </div>

          {/* Restored Supporting Documents Upload */}
          <div className="mt-5 pt-5 border-t border-brand-border/50">
            <label className="block text-xs font-semibold text-brand-muted uppercase tracking-wider mb-3">Supporting Documents</label>
            <div className="flex flex-wrap gap-2 items-center">
              {t.supportingFiles?.map((file, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-brand-border rounded-lg text-xs font-medium text-brand-text shadow-sm">
                  <SafeIcon icon={FiPaperclip} className="text-brand-muted" />
                  <span className="truncate max-w-[150px]">{file.name}</span>
                  <button onClick={() => removeFile(t.id, i)} className="ml-1 text-brand-muted hover:text-red-500 transition-colors">
                    <SafeIcon icon={FiX} />
                  </button>
                </span>
              ))}
              
              <label className="cursor-pointer inline-flex items-center justify-center px-4 py-1.5 border border-dashed border-brand-border rounded-lg text-xs font-medium text-brand-muted hover:text-brand-accent hover:border-brand-accent transition-all bg-white/50 hover:bg-white">
                <SafeIcon icon={FiPlus} className="mr-1.5" /> Add Document
                <input type="file" className="hidden" onChange={(e) => handleFileUpload(t.id, e)} />
              </label>
            </div>
          </div>

        </div>
      ))}
      <Button variant="outline" onClick={addTransaction} className="w-full border-dashed py-6 bg-white/50 hover:bg-white hover:border-brand-accent group">
        <SafeIcon icon={FiPlus} className="mr-2 group-hover:scale-110 transition-transform" /> 
        Add Transaction Line
      </Button>
    </div>
  );
};